import { useRef } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/joy";
import { motion, useInView } from "framer-motion";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import ArchitectureRoundedIcon from "@mui/icons-material/ArchitectureRounded";

import { Section, Container, SectionHeader, COLORS } from "../shared/ui";

const STEPS = [
  {
    icon: <EditNoteRoundedIcon sx={{ fontSize: 26 }} />,
    title: "Заявка",
    time: "2 минуты",
    desc: "Онлайн-форма или звонок. Рассчитаем точную стоимость за 5 минут.",
  },
  {
    icon: <EventAvailableRoundedIcon sx={{ fontSize: 26 }} />,
    title: "Согласование",
    time: "в день заявки",
    desc: "Согласуем дату и время с вами или собственником. Подпишем договор.",
  },
  {
    icon: <StraightenRoundedIcon sx={{ fontSize: 26 }} />,
    title: "Выезд на объект",
    time: "1–3 часа",
    desc: "Замерщик с Leica и Bosch. Фото, видео, все замеры. Без вашего присутствия.",
  },
  {
    icon: <ArchitectureRoundedIcon sx={{ fontSize: 26 }} />,
    title: "Выдача чертежей",
    time: "за 2–3 дня",
    desc: "Чертёж в вашей программе, PDF, фото и видео на Яндекс.Диске.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <Section id="process" py={{ xs: 10, md: 14 }} sx={{ background: "#fff" }}>
      <Container maxWidth={1180}>
        <SectionHeader
          eyebrow="Как устроена работа"
          title="4 шага до готового плана"
          subtitle="Прозрачно и без сюрпризов. На всех этапах вы видите статус в личном кабинете."
        />

        <Box ref={ref} sx={{ position: "relative" }}>
          {/* Соединительная линия (desktop) */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              top: 44,
              left: "12.5%",
              right: "12.5%",
              height: 2,
              background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.2) 10%, rgba(99,102,241,0.2) 90%, transparent)",
              zIndex: 0,
            }}
          />
          {/* Бегущая точка */}
          {inView && (
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                position: "absolute",
                top: 40,
                left: "12.5%",
                width: `calc(75% )`,
                height: 10,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              <motion.div
                initial={{ x: "0%" }}
                animate={{ x: "100%" }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${COLORS.brand2}, ${COLORS.brand})`,
                  boxShadow: `0 0 20px ${COLORS.brand}, 0 0 40px ${COLORS.brand2}`,
                  marginTop: -2,
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 2.5, md: 3 },
              position: "relative",
              zIndex: 2,
            }}
          >
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 32 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.12, ease: [0.4, 0, 0.2, 1] }}
              >
                <StepCard step={step} number={i + 1} />
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>
    </Section>
  );
}

function StepCard({ step, number }) {
  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: "18px",
        borderColor: "rgba(99,102,241,0.12)",
        background: "#fff",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: "rgba(99,102,241,0.35)",
          transform: "translateY(-4px)",
          boxShadow: "0 16px 40px rgba(99,102,241,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3, pt: 3.5 }}>
        {/* Номер шага */}
        <Box
          sx={{
            position: "absolute",
            top: -16,
            left: 24,
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 800,
            fontSize: "0.95rem",
            fontFamily: "monospace",
            boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
            zIndex: 2,
          }}
        >
          0{number}
        </Box>

        {/* Иконка */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "14px",
            background: "rgba(99,102,241,0.08)",
            color: COLORS.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            mt: 1,
          }}
        >
          {step.icon}
        </Box>

        <Typography
          level="title-md"
          sx={{ fontWeight: 800, color: COLORS.dark, mb: 0.25 }}
        >
          {step.title}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: COLORS.brand,
            fontWeight: 700,
            fontFamily: "monospace",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb: 1.25,
          }}
        >
          {step.time}
        </Typography>
        <Typography level="body-sm" sx={{ color: "neutral.600", lineHeight: 1.55 }}>
          {step.desc}
        </Typography>
      </CardContent>
    </Card>
  );
}